// Single source of truth for the allocation-rules download.
//
// Both the Recommendation-Rules "Download CSV" (pages/merchants/Rules.js) and the
// rule-history snapshot download (uploads/WeightZoneRulesBuilder/RuleHistory.js)
// build their file from here, so the two downloads are byte-identical and both match
// the ZONE_WEIGHT_RULE_UPLOAD template (sample_rule_upload.csv) — a true round-trip.
//
// Input: an array of rule objects, each with `conditions: [{fact, operator, value}]`
// and `result: { values: [courierCode, ...] }`.

export const UPLOAD_COLUMNS = [
  'Delivery Type', 'Zone', 'Min Weight (kg)', 'Max Weight (kg)',
  'Min Invoice Value(INR)', 'Max Invoice Value(INR)',
  'Pickup Pincode', 'Drop Pincode', 'Payment Mode',
  'Dispatch Mode', 'Shipment Type', 'RVP Reason',
  'Item Units', 'Unique SKU Count', 'SKU', 'Order Creation Time(IST)',
  'QC Type', 'ODA Value(s)', 'ABD Flag',
  ...Array.from({ length: 20 }, (_, i) => `Priority ${i + 1}`),
  'Channel Name',
];

const GT_OPS = ['GREATER_THAN', 'GREATER_THAN_EQUAL', '>', '>='];
const LT_OPS = ['LESS_THAN', 'LESS_THAN_EQUAL', '<', '<='];

// Rule pincodes are stored as "[110001,110002]" (or, on older snapshots, split into
// Part1/Part2). The upload template wants a ";"-separated list.
function pincodesToUpload(v) {
  if (v === null || v === undefined || v === '') return '';
  return String(v).trim().replace(/^\[|\]$/g, '').split(',').map((p) => p.trim()).filter(Boolean).join(';');
}

function buildUploadRows(rules) {
  return (rules || []).map((rule) => {
    const conds = rule.conditions || [];
    const val = (fact) => conds.find((c) => c.fact === fact)?.value ?? '';
    const rangeMin = (fact) => {
      const list = conds.filter((c) => c.fact === fact);
      return (list.find((c) => GT_OPS.includes(c.operator)) || list[0])?.value ?? '';
    };
    const rangeMax = (fact) => {
      const list = conds.filter((c) => c.fact === fact);
      return (list.find((c) => LT_OPS.includes(c.operator)) || list[1])?.value ?? '';
    };
    // Pincode: single (PICKUP_PINCODE) or split (…PART1/PART2) — support both.
    const pickup = [val('PICKUP_PINCODE'), val('PICKUP_PINCODE_PART1'), val('PICKUP_PINCODE_PART2')].filter(Boolean).join(',');
    const drop = [val('DROP_PINCODE'), val('DROP_PINCODE_PART1'), val('DROP_PINCODE_PART2')].filter(Boolean).join(',');
    const priorities = rule.result?.values || [];

    const row = {
      'Delivery Type': val('DELIVERY_TYPE'),
      'Zone': val('ZONE'),
      'Min Weight (kg)': rangeMin('WEIGHT'),
      'Max Weight (kg)': rangeMax('WEIGHT'),
      'Min Invoice Value(INR)': rangeMin('ORDER_VALUE'),
      'Max Invoice Value(INR)': rangeMax('ORDER_VALUE'),
      'Pickup Pincode': pincodesToUpload(pickup),
      'Drop Pincode': pincodesToUpload(drop),
      'Payment Mode': val('PAYMENT_MODE'),
      'Dispatch Mode': val('DISPATCH_MODE'),
      'Shipment Type': val('SHIPMENT_TYPE'),
      'RVP Reason': val('RVP_REASON'),
      'Item Units': val('ITEM_UNITS'),
      'Unique SKU Count': val('UNIQUE_SKU_COUNT'),
      // SKU rule list is stored as "SKU001" or "[SKU001,SKU002]"; the template wants "SKU001;SKU002".
      'SKU': pincodesToUpload(val('SKU')),
      'Order Creation Time(IST)': val('ORDER_CREATION_TIME'),
      'QC Type': val('QC_TYPE'),
      'ODA Value(s)': val('ODA_VALUE'),
      'ABD Flag': val('ABD_FLAG'),
      'Channel Name': val('CHANNEL_NAME'),
    };
    for (let i = 0; i < 20; i++) row[`Priority ${i + 1}`] = priorities[i] || '';
    return row;
  });
}

function csvEscape(v) {
  const s = v === null || v === undefined ? '' : String(v);
  return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
}

export function rulesToUploadCsv(rules) {
  const rows = buildUploadRows(rules);
  const header = UPLOAD_COLUMNS.map(csvEscape).join(',');
  const body = rows.map((r) => UPLOAD_COLUMNS.map((c) => csvEscape(r[c])).join(',')).join('\n');
  return header + '\n' + body;
}

export function downloadRulesCsv(rules, filename) {
  const csv = rulesToUploadCsv(rules);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'Recommendation_Rules.csv';
  a.click();
  URL.revokeObjectURL(url);
}
